/* @flow */
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import idx from 'idx';

import PeopleListTable from './PeopleListTable.jsx';
import CenteredContainer from 'components/CenteredContainer.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Text from 'components/Text.jsx';
import GET_ORG_MEMBERS_QUERY from './queries/OrganizationMembersQuery.gql';
import { ALL_ROLES } from 'utils/roleHelpers.js';

import type {
  GetOrganizationMembers,
  GetOrganizationMembers_organizations_members_members,
} from './queries/__generated__/GetOrganizationMembers.js';

import type { SortDirection } from 'components/ComposableTable';
import type { EntityType, UserRole, GroupInfo } from './PeopleList.jsx';
import type { ApolloQueryResult } from 'apollo-client';

export type PeopleListRow = GetOrganizationMembers_organizations_members_members;

type Props = {
  entityType: EntityType,
  entityUuid: string,
  groupInfo: GroupInfo,
  grade: ?string,
  query: ?string,
  role: UserRole,
  rostered: ?string,
  hasNextPage: boolean,
  fetchMore: () => Promise<any>,
  refetch: (?{ attributeName: ?string, direction: ?SortDirection }) => Promise<any>,
  members: PeopleListRow[],
  loading: boolean,
  error: errorFragmentFragment,
  onPersonClicked: Object => void,
  onUserAddedToSelection: PeopleListRow => void,
  onUserRemovedFromSelection: PeopleListRow => void,
  reloadQuery: boolean,
  selectedUserIds: number[],
};

class PeopleListTableDataContainer extends React.Component<Props> {
  static defaultProps = {
    onPersonClicked: () => {},
  };

  componentWillReceiveProps(nextProps: Props) {
    const { reloadQuery, refetch } = this.props;
    if (!reloadQuery && nextProps.reloadQuery) {
      refetch();
    }
  }

  handleSort = (sortColumn: string, sortDirection: ?SortDirection) => {
    this.props.refetch({ attributeName: sortColumn, direction: sortDirection });
  };

  errorMessage = (error: errorFragmentFragment) => {
    return (
      <CenteredContainer className="PeopleList-body">
        {error && (
          <ErrorReporter error="PeopleListTableDataContainer - couldn't load data">
            <Text>Error loading data</Text>
          </ErrorReporter>
        )}
      </CenteredContainer>
    );
  };

  userRole = () => {
    const { query, role } = this.props;

    if (query && query.length > 0) {
      return 'all';
    }

    return role;
  };

  render() {
    const {
      loading,
      error,
      hasNextPage,
      fetchMore,
      entityUuid,
      groupInfo,
      members,
      onPersonClicked,
      onUserAddedToSelection,
      onUserRemovedFromSelection,
      selectedUserIds,
    } = this.props;

    if (error) {
      return this.errorMessage(error);
    }

    return (
      <PeopleListTable
        entityUuid={entityUuid}
        hasNextPage={hasNextPage}
        loading={loading}
        fetchMore={fetchMore}
        members={members}
        groupInfo={groupInfo}
        role={this.userRole()}
        selectedUserIds={selectedUserIds}
        onPersonClicked={onPersonClicked}
        onUserAddedToSelection={onUserAddedToSelection}
        onUserRemovedFromSelection={onUserRemovedFromSelection}
        onSort={this.handleSort}
      />
    );
  }
}

const generateProps = (
  data: ApolloQueryResult<GetOrganizationMembers>,
  contentsKey: 'organizations' | 'groups'
) => {
  return {
    fetchMore: () =>
      data.fetchMore({
        variables: {
          after: idx(data[contentsKey], _ => _[0].members.pageInfo.endCursor),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetchMoreContents = idx(fetchMoreResult[contentsKey], _ => _[0]);
          const prevContents = idx(prev[contentsKey], _ => _[0]);

          if (!fetchMoreContents || !prevContents) {
            return prev;
          }

          const newResult = Object.assign({}, prev);

          // Concat member arrays and assign to new object members key
          const newMembersContents = Object.assign({}, fetchMoreContents.members, {
            members: [...prevContents.members.members, ...fetchMoreContents.members.members],
          });

          // Assign { members: [] } to outer members key in structure.
          // Structure is
          // members: {
          //   members: [],
          //   pageInfo: {},
          // }
          const newResultContents = Object.assign({}, fetchMoreContents, {
            members: newMembersContents,
          });

          // Assign new result to the first element in the organizations array since
          // most outer structure is
          // organizations: [
          //  {
          //    uuid
          //    members: { ... as above }
          //  }
          // ]
          newResult[contentsKey] = [newResultContents];

          return newResult;
        },
      }),
    hasNextPage: idx(data[contentsKey], _ => _[0].members.pageInfo.hasNextPage),
    members: idx(data[contentsKey], _ => _[0].members.members),
    loading: data.loading,
    error: data.error,
    refetch: data.refetch,
  };
};

const withOrganizationMembers = graphql(GET_ORG_MEMBERS_QUERY, {
  options: ({ entityUuid, grade, query, role, rostered, attributeName, direction }) => {
    const isSearching = !!(query && query.length > 0);

    return {
      variables: {
        orgUuid: entityUuid,
        first: 20,
        after: null,
        query,
        roles: isSearching ? ALL_ROLES : [role],
        grade: isSearching ? null : grade,
        rostered: isSearching ? 'all' : rostered,
        attributeName: attributeName || 'id',
        direction: direction || 'asc',
      },
    };
  },
  skip: ({ entityType }) => entityType !== 'organization',
  props: ({ data }) => generateProps(data, 'organizations'),
});

export { PeopleListTableDataContainer };

export default compose(withOrganizationMembers)(PeopleListTableDataContainer);
